import React, { useEffect } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { CssBaseline, Box, useMediaQuery } from '@mui/material'; // useMediaQuery 추가
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import { Outlet } from 'react-router-dom';

import LeftDrawer from './LeftDrawer';
import DrawerHeader from './DrawerHeader';
import NotiContainer from '../../../containers/common/header/NotiContainer';
import UserMenuContainer from '../../../containers/common/header/UserMenuContainer';
import LinkBoxContainer from '../../../containers/common/header/LinkBoxContainer';
import ProjectCounterContainer from '../../../containers/common/projectCounter/ProjectCounterContainer';

const drawerWidth = 240;

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    // 데스크탑 기본: 드로어 너비만큼 왼쪽 마진을 당겨서 숨김
    marginLeft: `-${drawerWidth}px`,
    ...(open && {
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
    }),
    // [모바일] md 미만에서는 본문 고정 (Drawer가 위로 덮음)
    [theme.breakpoints.down('md')]: {
      marginLeft: 0,
      marginTop: theme.spacing(-1.5),
      padding: theme.spacing(1.5)
    },
  }),
);

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer - 1, 
  [theme.breakpoints.up('md')]: {
    zIndex: theme.zIndex.drawer + 1, // 데스크탑은 상단바가 항상 위
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
      width: `calc(100% - ${drawerWidth}px)`,
      marginLeft: `${drawerWidth}px`,
      transition: theme.transitions.create(['margin', 'width'], {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
    }),
    },
}));

const DrawerTemplate = (props) => {
  const theme = useTheme();
  // md(900px) 미만이면 true 반환
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // 초기 상태: 모바일이면 false, 데스크탑이면 true
  const [open, setOpen] = React.useState(!isMobile);

  // 화면 크기 변화 감지하여 상태 업데이트
  useEffect(() => {
    setOpen(!isMobile);
  }, [isMobile]);

  const handleDrawerToggle = () => {
  setOpen(!open);
};

// 2. 본문이나 메뉴 클릭 시 호출될 핸들러 (모바일에서만 작동)
const handleDrawerClose = () => {
 // if (isMobile) {
    setOpen(false);
  //} 
};


  return (
    <Box sx={{ display: 'flex',  }}>
      <CssBaseline />

      <AppBar position="fixed" open={open} color="default" >
        <Toolbar variant="dense" 
          sx={{
            // [모바일] md 미만(xs, sm)에서만 40px로 축소
            '@media (max-width: 899px)': {
              minHeight: '45px !important',
              height: '45px',
              px: 1, // 좌우 여백 축소
            },
          }}
        >
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerToggle}
            edge="start"
            sx={{ mr: 2, ...(open && { display: 'none' }) }}
          >
            <MenuOpenIcon />
          </IconButton>
          
          <Box sx={{ mx: 1, display: { xs: 'none', md: 'block' } }}>
            <NotiContainer />
          </Box>
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <ProjectCounterContainer />
          </Box>
          
          <Box sx={{ flexGrow: 1 }} />
          
          <Box sx={{ display: 'flex', alignItems: 'center', pl: 1, py: 1 }}>
            <Box sx={{ mr: 1 }}><UserMenuContainer /></Box>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <LinkBoxContainer />
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      <LeftDrawer
        theme={theme}
        drawerWidth={drawerWidth}
        open={open}
        handleDrawerClose={handleDrawerClose}
        // 모바일 여부를 props로 전달하여 LeftDrawer 내부에서 variant="temporary" 처리가 가능하도록 함
        isMobile={isMobile} 
      />

      <Main open={open} onClick={isMobile ? handleDrawerClose : undefined}>
        {/* DrawerHeader는 Toolbar 높이만큼 공간을 확보해줍니다 */}
        <DrawerHeader />
        <Box> 
          <Outlet />
        </Box>
      </Main>
    </Box>
  );
};

export default DrawerTemplate;
